import { FC, ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import Barcode from 'react-barcode';

export const FIELD_BARCODE_WIDTH_PX = 200;

export const fieldBarcodeGroupSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gap: 1,
    border: '1px solid',
    borderColor: 'grey.400',
    borderRadius: 1,
    p: 1.5,
    boxSizing: 'border-box',
    '@media print': {
        display: 'block',
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
    },
};

interface IDocumentField {
    ControlType?: number | string;
    ControlFormat?: Record<string, unknown> | null;
}

const getBarcodeValue = (value: unknown): string | null => {
    if (value == null || value === '') {
        return null;
    }

    return String(value);
};

const shouldShowBarcode = (field?: IDocumentField | null): boolean => {
    return field?.ControlFormat?.Barcode === 'true';
};

export const fieldDisplaysBarcode = (field?: IDocumentField | null, value?: unknown): boolean => {
    return shouldShowBarcode(field) && getBarcodeValue(value) != null;
};

interface IFieldBarcodeProps {
    value: string;
}

const FieldBarcode = ({ value }: IFieldBarcodeProps) => {
    return (
        <Box
            sx={{
                alignSelf: 'flex-start',
                width: FIELD_BARCODE_WIDTH_PX,
                maxWidth: '100%',
                '& svg': {
                    width: '100%',
                    display: 'block',
                },
            }}
        >
            <Barcode value={value} width={0.5} height={50} displayValue={false} />
        </Box>
    );
};

interface IWrapFieldWithBarcodeProps {
    field?: IDocumentField | null;
    value: unknown;
    children: ReactNode;
    label?: ReactNode;
}

export const WrapFieldWithBarcode: FC<IWrapFieldWithBarcodeProps> = ({ field, value, children, label }) => {
    if (!fieldDisplaysBarcode(field, value)) {
        return (
            <>
                {label}
                {children}
            </>
        );
    }

    const barcodeValue = getBarcodeValue(value);
    if (!barcodeValue) {
        return (
            <>
                {label}
                {children}
            </>
        );
    }

    return (
        <Box className="field-barcode-group" sx={fieldBarcodeGroupSx}>
            {label}
            <Box sx={{ width: '100%', alignSelf: 'stretch', textAlign: 'left' }}>{children}</Box>
            <FieldBarcode value={barcodeValue} />
        </Box>
    );
};
